<template>
  <div class="hx-bag">
    <section class="filter">
      <van-field
        readonly
        clickable
        :right-icon="orderStartTime ? 'clear' : ''"
        v-model="orderStartTime"
        label="订单开始时间"
        placeholder="请选择订单开始时间"
        @click="openStartTimePicker"
        @click-right-icon.stop="clearOrderStartTime"
      />
      <van-popup v-model="showStartTimePicker" position="bottom">
        <van-datetime-picker
          v-model="startPickerValue"
          type="datetime"
          title="选择开始时间"
          :min-date="minOrderDate"
          @confirm="onConfirmOrderStartTime"
          @cancel="showStartTimePicker = false"
        />
      </van-popup>
      <van-field
        readonly
        clickable
        :right-icon="orderEndTime ? 'clear' : ''"
        v-model="orderEndTime"
        label="订单结束时间"
        placeholder="请选择订单结束时间"
        @click="openEndTimePicker"
        @click-right-icon.stop="clearOrderEndTime"
      />
      <van-popup v-model="showEndTimePicker" position="bottom">
        <van-datetime-picker
          v-model="endPickerValue"
          type="datetime"
          title="选择结束时间"
          :min-date="minOrderDate"
          @confirm="onConfirmOrderEndTime"
          @cancel="showEndTimePicker = false"
        />
      </van-popup>
      <van-field
        v-model.trim="gcName"
        maxlength="50"
        clearable
        label="商品名称"
        placeholder="请输入商品名称"
        @keyup.enter="onSearch"
      />
      <div class="filter-actions">
        <van-button round block type="info" size="small" @click="onSearch">
          查询
        </van-button>
      </div>
    </section>

    <van-list
      v-model="loading"
      :finished="finished"
      :error.sync="error"
      :finished-text="list.length ? '没有更多了' : ''"
      error-text="加载失败，点击重新加载"
      @load="onLoad"
    >
      <div class="list">
        <div
          v-for="(item, index) in list"
          :key="`${item.ordSubNo || item.ordNo || ''}-${index}`"
          class="group"
        >
          <div class="center">
            <div class="item">
              <div class="label">订单号:</div>
              <div class="value">{{ item.ordNo || '—' }}</div>
            </div>
            <div class="item">
              <div class="label">商品名称:</div>
              <div class="value">{{ item.gcName || '—' }}</div>
            </div>
            <div class="item">
              <div class="label">面值:</div>
              <div class="value">
                {{ formatFaceAmount(item.gcDenomination) }}
              </div>
            </div>
            <div class="item">
              <div class="label">券码类型:</div>
              <div class="value">{{ getCodeTypeText(item.codeType) }}</div>
            </div>
            <div class="item">
              <div class="label">订单时间:</div>
              <div class="value">{{ item.sendTime || '—' }}</div>
            </div>
            <div class="item">
              <div class="label">有效期:</div>
              <div class="value">{{ item.expireTime || '—' }}</div>
            </div>
            <div v-show="item.cardNo" class="item">
              <div class="label">卡号:</div>
              <div class="value">{{ item.cardNo }}</div>
              <div
                class="copy"
                v-clipboard:copy="item.cardNo"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                复制
              </div>
            </div>
            <div v-show="item.cardPwd" class="item">
              <div class="label">卡密:</div>
              <div class="value">{{ item.cardPwd }}</div>
              <div
                class="copy"
                v-clipboard:copy="item.cardPwd"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                复制
              </div>
            </div>
            <div v-show="item.verifyCode" class="item">
              <div class="label">验证码:</div>
              <div class="value">{{ item.verifyCode }}</div>
              <div
                class="copy"
                v-clipboard:copy="item.verifyCode"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                复制
              </div>
            </div>
            <div v-show="item.shortUrl" class="item">
              <div class="label">短链:</div>
              <div class="value link" @click="openLink(item.shortUrl)">
                {{ item.shortUrl }}
              </div>
              <div
                class="copy"
                v-clipboard:copy="item.shortUrl"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                复制
              </div>
            </div>
            <div v-show="item.redeemCode" class="item">
              <div class="label">兑换码:</div>
              <div class="value">{{ item.redeemCode }}</div>
              <div
                class="copy"
                v-clipboard:copy="item.redeemCode"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                复制
              </div>
            </div>
            <div v-show="Number(item.codeType) === 4" class="item">
              <div class="label">兑换状态:</div>
              <div class="value">
                {{ getRedeemStatusText(item.redeCodeStatus) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-list>

    <van-empty v-if="showEmpty" description="暂无卡包记录" />
  </div>
</template>

<script>
import moment from 'moment'
import { getToken, getOrderPages } from '@/api/haixiaCardBag'

// codeType：0卡号卡密 1卡密 2短链 3短链验证码 4兑换码
const CODE_TYPE_MAP = {
  0: '卡号卡密',
  1: '卡密',
  2: '短链',
  3: '短链验证码',
  4: '兑换码'
}

// redeCodeStatus：0未使用 1兑换中 2已使用 3已过期 4已出库待发放
// 5已退货 6已延期 7已冻结 8异常 9到期（预扣回款）
const REDEEM_STATUS_MAP = {
  0: '未使用',
  1: '兑换中',
  2: '已使用',
  3: '已过期',
  4: '已出库待发放',
  5: '已退货',
  6: '已延期',
  7: '已冻结',
  8: '异常',
  9: '到期'
}

export default {
  name: 'HaiXiaCardBagOrderList',
  data() {
    return {
      gcName: '',
      orderStartTime: '',
      orderEndTime: '',
      list: [],
      loading: false,
      finished: false,
      error: false,
      pageNum: 1,
      pageSize: 10,
      queryId: 0,
      showStartTimePicker: false,
      showEndTimePicker: false,
      startPickerValue: new Date(),
      endPickerValue: new Date(),
      minOrderDate: new Date(2020, 0, 1)
    }
  },
  computed: {
    showEmpty() {
      return this.finished && !this.loading && !this.error && !this.list.length
    }
  },
  created() {
    if (!getToken()) {
      this.$router.replace({ name: 'HaiXiaCardBagLogin' })
    }
  },
  methods: {
    // 后端金额单位为万分之一元，展示时除以 10000
    formatFaceAmount(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = Number(value)
      if (Number.isNaN(num)) return '—'
      return String(parseFloat((num / 10000).toFixed(4)))
    },
    getCodeTypeText(codeType) {
      if (codeType === null || codeType === undefined || codeType === '') {
        return '—'
      }
      return CODE_TYPE_MAP[codeType] !== undefined
        ? CODE_TYPE_MAP[codeType]
        : String(codeType)
    },
    getRedeemStatusText(status) {
      if (status === null || status === undefined || status === '') return '—'
      return REDEEM_STATUS_MAP[status] !== undefined
        ? REDEEM_STATUS_MAP[status]
        : String(status)
    },
    buildParam() {
      const param = {}
      if (this.gcName) param.gcName = this.gcName
      if (this.orderStartTime) param.startTime = this.orderStartTime
      if (this.orderEndTime) param.endTime = this.orderEndTime
      return param
    },
    validateDate() {
      if (
        this.orderStartTime &&
        this.orderEndTime &&
        this.orderStartTime > this.orderEndTime
      ) {
        this.$toast('开始时间不能晚于结束时间')
        return false
      }
      return true
    },
    async onLoad() {
      if (!getToken()) {
        this.loading = false
        this.finished = true
        return
      }
      const queryId = this.queryId
      const pageNum = this.pageNum
      try {
        this.error = false
        const { data } = await getOrderPages({
          pageNum,
          pageSize: this.pageSize,
          param: this.buildParam()
        })
        if (queryId !== this.queryId) return
        const records = (data && data.records) || []
        const total = data && data.total != null ? Number(data.total) : 0
        this.list = this.list.concat(records)
        this.pageNum = pageNum + 1
        if (records.length < this.pageSize || this.list.length >= total) {
          this.finished = true
        }
      } catch (error) {
        if (queryId !== this.queryId) return
        this.error = true
      } finally {
        if (queryId === this.queryId) this.loading = false
      }
    },
    resetAndLoad() {
      this.queryId += 1
      this.list = []
      this.pageNum = 1
      this.finished = false
      this.error = false
      this.loading = true
      this.onLoad()
    },
    onSearch() {
      if (!this.validateDate()) return
      this.resetAndLoad()
    },
    openStartTimePicker() {
      this.startPickerValue = this.orderStartTime
        ? moment(this.orderStartTime, 'YYYY-MM-DD HH:mm:ss').toDate()
        : new Date()
      this.showStartTimePicker = true
    },
    openEndTimePicker() {
      this.endPickerValue = this.orderEndTime
        ? moment(this.orderEndTime, 'YYYY-MM-DD HH:mm:ss').toDate()
        : new Date()
      this.showEndTimePicker = true
    },
    onConfirmOrderStartTime(value) {
      this.orderStartTime = moment(value).format('YYYY-MM-DD HH:mm:00')
      this.showStartTimePicker = false
    },
    onConfirmOrderEndTime(value) {
      this.orderEndTime = moment(value).format('YYYY-MM-DD HH:mm:59')
      this.showEndTimePicker = false
    },
    clearOrderStartTime() {
      this.orderStartTime = ''
    },
    clearOrderEndTime() {
      this.orderEndTime = ''
    },
    openLink(url) {
      if (!url || !/^https?:\/\//i.test(String(url))) return
      window.location.href = url
    },
    onCopy() {
      this.$toast.success('复制成功')
    },
    onCopyError() {
      this.$toast.fail('复制失败')
    }
  }
}
</script>

<style lang="less" scoped>
.hx-bag {
  min-height: 100vh;
  background: #ecedf0;
  padding-bottom: 24px;
  box-sizing: border-box;
}

.filter {
  position: sticky;
  top: 0;
  z-index: 19;
  padding-bottom: 12px;
  background: #fff;
  box-shadow: 0 4px 11px 0 rgba(178, 178, 178, 0.18);
}

.filter-actions {
  width: 50%;
  margin: 16px auto 0;
}

.list {
  padding: 12px 15px 0;
  box-sizing: border-box;
}

.group {
  margin: 0 0 8px;
  padding-top: 16px;
  background: #fff;
  border-radius: 15px;

  .center {
    box-sizing: border-box;

    .item {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 0 20px 16px;
      font-size: 12px;
      box-sizing: border-box;

      .label {
        width: 72px;
        color: #000;
      }

      .value {
        flex: 1;
        width: 100%;
        color: #666;
        word-break: break-all;

        &.link {
          color: #1989fa;
          text-decoration: underline;
        }
      }

      .copy {
        width: 48px;
        height: 24px;
        margin-left: 8px;
        border-radius: 10px;
        background: #1989fa;
        font-size: 12px;
        line-height: 24px;
        color: #fff;
        text-align: center;
      }
    }
  }
}
</style>
